import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Disclosure,
  DisclosurePanel,
  DisclosureButton,
} from "@headlessui/react";
import { FaBars, FaUser, FaHeart } from "react-icons/fa";
import { FaBagShopping, FaXmark } from "react-icons/fa6";
import { toast } from "react-toastify";
import Spinner from "../common_Components/Spinner";
import Logo from "../../assets/Nutraj_Logo_White.png";
import GreenLogo from "../../assets/Nutraj Logo F 22.png";
import Model from "../common_Components/Model";
import CommonBtn from "../common_Components/CommonBtn";
import { Routing } from "../shared/Routing";
import {
  sendOtpToEmail,
  verifyOtpApi,
  sendOtpToSignUpEmail,
  registerUserSendOtp,
  googleAuthApi,
} from "../services/Login/auth";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthContext";
import { GetAllCart } from "../services/Cart/Cart";

const RESEND_OTP_SECONDS = 60;

const Header = () => {
  const navigation = [
    { name: "Home", href: Routing.Initial },
    { name: "About", href: Routing.About },
    { name: "Products", href: Routing.Products },
    { name: "Premium Freeze-Dried", href: Routing.PremiumFreezeDried },
    { name: "Blog", href: Routing.Blog },
    { name: "Gallery", href: Routing.Gallery },
    { name: "Contact", href: Routing.Contact },
  ];
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { showLoginModal, isSignUp, openLoginModal, closeLoginModal } = useAuth();

  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localIsSignUp, setLocalIsSignUp] = useState(isSignUp); // Initialize with context value

  
  const [step, setStep] = useState(1); // Step 1: Email, Step 2: OTP (for login) or All Fields (for signup)
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  // Ensure userEmail is always a string, even if stored as object in localStorage
  const getStoredEmail = () => {
    const stored = localStorage.getItem("userEmail");
    if (!stored) return "";
    try {
      // Try to parse if it's JSON
      const parsed = JSON.parse(stored);
      return typeof parsed === "string" ? parsed : String(parsed || "");
    } catch {
      // If not JSON, return as string
      return String(stored || "");
    }
  };

  const getStoredUser = () => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };
  
  const [userEmail, setUserEmail] = useState(getStoredEmail()); // Get email from localStorage if already logged in
  const [userProfilePhoto, setUserProfilePhoto] = useState(getStoredUser()?.profile_photo || "");
  const [dropdownOpen, setDropdownOpen] = useState(false); // For dropdown menu visibility
  const [otpResendSeconds, setOtpResendSeconds] = useState(0);
  const [cartCount, setCartCount] = useState(0);

  const refreshCartCount = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setCartCount(0);
      return;
    }
    try {
      const res = await GetAllCart();
      if (res?.success && Array.isArray(res?.data?.items)) {
        const totalQty = res.data.items
          .filter((item) => item?.product)
          .reduce((sum, item) => sum + (item.quantity || 0), 0);
        setCartCount(totalQty);
      } else {
        setCartCount(0);
      }
    } catch {
      setCartCount(0);
    }
  };

  // Sync local state with context state
  useEffect(() => {
    setLocalIsSignUp(isSignUp);
  }, [isSignUp]);

  const notifyAuthChange = () => {
    window.dispatchEvent(new Event("nutrajun-auth-change"));
  };

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY >= 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setDropdownOpen(false); // Close dropdown on route change
  }, [pathname]);

  useEffect(() => {
    refreshCartCount();
    const onCart = () => refreshCartCount();
    const onAuth = () => {
      refreshCartCount();
      setUserEmail(getStoredEmail());
      setUserProfilePhoto(getStoredUser()?.profile_photo || "");
    };
    window.addEventListener("nutrajun-cart-change", onCart);
    window.addEventListener("nutrajun-auth-change", onAuth);
    return () => {
      window.removeEventListener("nutrajun-cart-change", onCart);
      window.removeEventListener("nutrajun-auth-change", onAuth);
    };
  }, [pathname]);

  useEffect(() => {
    if (!showLoginModal) {
      setOtpResendSeconds(0);
    }
  }, [showLoginModal]);

  useEffect(() => {
    if (otpResendSeconds <= 0) return undefined;
    const t = setTimeout(() => setOtpResendSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [otpResendSeconds]);

  // Helper function to extract email string from various formats
  const extractEmailString = (value) => {
    console.log("extractEmailString - Input value:", value, "Type:", typeof value);
    
    if (!value) {
      console.log("extractEmailString - Value is falsy, returning empty string");
      return "";
    }
    
    // If it's already a string, return it
    if (typeof value === "string") {
      const trimmed = value.trim();
      console.log("extractEmailString - String value, returning:", trimmed);
      return trimmed;
    }
    
    // If it's an object, try to extract email property
    if (typeof value === "object" && value !== null) {
      console.log("extractEmailString - Object value, keys:", Object.keys(value));
      
      // Check common email property names
      const emailFromObject = value.email 
        || value.Email 
        || value.EMAIL
        || value.userEmail
        || value.user_email
        || value.mail
        || value.e
        || value.value
        || value.data;
      
      if (emailFromObject) {
        const extracted = typeof emailFromObject === "string" ? emailFromObject.trim() : String(emailFromObject).trim();
        console.log("extractEmailString - Found email in object:", extracted);
        return extracted;
      }
      
      // If object has only one property and it's a string, it might be the email
      const keys = Object.keys(value);
      if (keys.length === 1 && typeof value[keys[0]] === "string") {
        const potentialEmail = value[keys[0]].trim();
        // Basic email validation
        if (potentialEmail.includes("@") && potentialEmail.includes(".")) {
          console.log("extractEmailString - Found email in single property:", potentialEmail);
          return potentialEmail;
        }
      }
      
      // Try to find any string property that looks like an email
      for (const key in value) {
        if (typeof value[key] === "string" && value[key].includes("@") && value[key].includes(".")) {
          console.log("extractEmailString - Found email-like string in property", key, ":", value[key]);
          return value[key].trim();
        }
      }
      
      console.log("extractEmailString - No email found in object, returning empty string");
      return "";
    }
    
    // Fallback: convert to string
    const str = String(value);
    // If it's "[object Object]", return empty string
    if (str === "[object Object]" || str === "[object Object]") {
      console.log("extractEmailString - String conversion resulted in [object Object]");
      return "";
    }
    const trimmed = str.trim();
    console.log("extractEmailString - Converted to string:", trimmed);
    return trimmed;
  };

// New user send otp

  const handleSignUp = async () => {
    // Extract email string properly
    const emailToSend = extractEmailString(email);
    console.log("SignUp - Original email:", email, "Type:", typeof email);
    console.log("SignUp - Extracted email:", emailToSend);
    
    // Validate email
    if (!emailToSend) {
      return toast.error("Please enter a valid email.");
    }
    
    if (emailToSend === "[object Object]" || !emailToSend.includes("@")) {
      return toast.error("Please enter a valid email address.");
    }
    setLoading(true);
    try {
      const res = await sendOtpToSignUpEmail({ email: emailToSend }); // Your API function to send OTP
      if (res?.success) {
        toast.success(res.message);
        setStep(2); // Move to OTP verification for signup
        setOtpResendSeconds(RESEND_OTP_SECONDS);
        setLocalIsSignUp(true); // Set localIsSignUp to true for the signup process
      } else {
        toast.error(res.message || "Failed to send OTP for signup");
      }
    } catch (err) {
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (customEmail) => {
    // Get email from customEmail parameter or email state
    let emailValue = customEmail || email;
    
    console.log("SendOTP - customEmail:", customEmail, "Type:", typeof customEmail);
    console.log("SendOTP - email state:", email, "Type:", typeof email);
    console.log("SendOTP - emailValue (before extraction):", emailValue, "Type:", typeof emailValue);
    
    // If emailValue is already a string and not empty, use it directly
    if (typeof emailValue === "string" && emailValue.trim() !== "") {
      const emailToSend = emailValue.trim();
      console.log("SendOTP - Using string email directly:", emailToSend);
      
      if (!emailToSend.includes("@")) {
        return toast.error("Please enter a valid email address.");
      }
      
      setLoading(true);
      console.log("Email being sent:", emailToSend);
      
      try {
        const res = await sendOtpToEmail({ email: emailToSend });
        if (res?.success) {
          toast.success(res.message);
          setStep(2);
          setOtpResendSeconds(RESEND_OTP_SECONDS);
        } else {
          if (res?.message && res.message.toLowerCase().includes("user not found")) {
            toast.error("Please signup first");
          } else {
            toast.error(res.message || "Failed to send OTP");
          }
        }
      } catch (err) {
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // If not a string, try to extract it
    const emailToSend = extractEmailString(emailValue);
    
    console.log("SendOTP - Extracted email:", emailToSend);
    
    // Validate email
    if (!emailToSend || emailToSend.trim() === "") {
      console.error("SendOTP - Email extraction failed, emailToSend is empty");
      return toast.error("Please enter a valid email.");
    }
    
    if (emailToSend === "[object Object]" || !emailToSend.includes("@")) {
      console.error("SendOTP - Invalid email format:", emailToSend);
      return toast.error("Please enter a valid email address.");
    }
    
    // Update email state if customEmail is provided and different
    if (customEmail && emailToSend !== email) {
      setEmail(emailToSend);
    }

    setLoading(true);
    console.log("Email being sent:", emailToSend);

    try {
      const res = await sendOtpToEmail({ email: emailToSend });
      if (res?.success) {
        toast.success(res.message);
        setStep(2);
        setOtpResendSeconds(RESEND_OTP_SECONDS);
      } else {
        if (res?.message && res.message.toLowerCase().includes("user not found")) {
          toast.error("Please signup first");
        } else {
          toast.error(res.message || "Failed to send OTP");
        }
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpResendSeconds > 0 || loading) return;
    const emailToSend = extractEmailString(email);
    if (!emailToSend || !emailToSend.includes("@")) {
      return toast.error("Please enter a valid email.");
    }
    setLoading(true);
    try {
      const res = localIsSignUp
        ? await sendOtpToSignUpEmail({ email: emailToSend })
        : await sendOtpToEmail({ email: emailToSend });
      if (res?.success) {
        toast.success(res.message || "OTP resent");
        setOtpResendSeconds(RESEND_OTP_SECONDS);
      } else {
        toast.error(res?.message || "Failed to resend OTP");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and complete signup for new users
  const handleVerifySignUpOtp = async () => {
    if (!otp) return toast.error("Please enter OTP.");

    setLoading(true);
    // Extract email string properly
    const emailToSend = extractEmailString(email);
    if (!emailToSend || emailToSend === "[object Object]" || !emailToSend.includes("@")) {
      setLoading(false);
      return toast.error("Please enter a valid email.");
    }
    
    const body = {
      email: emailToSend,
      otp,
    };

    try {
      const res = await registerUserSendOtp(body);
      if (res?.success) {
        toast.success("Account created successfully! Please complete your profile.");
        setStep(1);
        setEmail("");
        setOtp("");

        // Ensure email is a string
        const emailString = extractEmailString(email);
        
        const mergedUser = {
          ...(res.data || {}),
          email: emailString,
        };

        setUserEmail(emailString);
        setUserProfilePhoto(mergedUser.profile_photo || "");
        localStorage.setItem("userEmail", emailString);
        localStorage.setItem("user", JSON.stringify(mergedUser));
        if (res.token) {
          localStorage.setItem("token", JSON.stringify(res.token));
        }
        notifyAuthChange();
        closeLoginModal();
        
        // Redirect to profile page to complete profile
        navigate(Routing.Profile);
      } else {
        toast.error(res?.message || "Failed to create account");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP for existing users (login)
  const handleVerifyLoginOtp = async () => {
    if (!otp) return toast.error("Please enter OTP.");

    setLoading(true);
    // Extract email string properly
    const emailToSend = extractEmailString(email);
    if (!emailToSend || emailToSend === "[object Object]" || !emailToSend.includes("@")) {
      setLoading(false);
      return toast.error("Please enter a valid email.");
    }
    
    try {
      const res = await verifyOtpApi({ email: emailToSend, otp });
      if (res?.success) {
        toast.success("Logged in successfully");
        setStep(1);
        setEmail("");
        setOtp("");

        // Ensure email is a string
        const emailString = extractEmailString(email);
        
        const mergedUser = {
          ...(res.data || {}),
          email: emailString,
        };

        if (!mergedUser.name && res?.data?.full_name) {
          mergedUser.name = res.data.full_name;
        }

        if (
          !mergedUser.mobile_number &&
          (res?.data?.mobile_number || res?.data?.phone)
        ) {
          mergedUser.mobile_number = res?.data?.mobile_number || res?.data?.phone;
        }
        
        setUserEmail(emailString);
        setUserProfilePhoto(mergedUser.profile_photo || "");
        localStorage.setItem("userEmail", emailString);
        localStorage.setItem("user", JSON.stringify(mergedUser));
        if (res.token) {
          localStorage.setItem("token", JSON.stringify(res.token));
        }
        notifyAuthChange();
        closeLoginModal();
      } else {
        toast.error(res?.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Direct login with Google OAuth
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      if (!credentialResponse?.credential) {
        toast.error("Google authentication failed");
        return;
      }

      const decoded = jwtDecode(credentialResponse.credential);
      if (!decoded?.email) {
        toast.error("Email not found in Google account");
        return;
      }

      setLoading(true);
      const emailString = String(decoded.email).trim();
      const nameString = decoded.name || "";
      const pictureUrl = decoded.picture || "";

      const res = await googleAuthApi({
        email: emailString,
        name: nameString,
        picture: pictureUrl,
      });

      if (res?.success) {
        toast.success(res.message || "Logged in successfully with Google");
        setStep(1);
        setEmail("");
        setOtp("");

        const mergedUser = {
          ...(res.data || {}),
          email: res.data?.email || emailString,
          name: res.data?.name || nameString,
          profile_photo: res.data?.profile_photo || pictureUrl,
          id: res.data?.id || res.data?._id,
          _id: res.data?._id || res.data?.id,
        };

        setUserEmail(mergedUser.email);
        setUserProfilePhoto(mergedUser.profile_photo || "");
        localStorage.setItem("userEmail", mergedUser.email);
        localStorage.setItem("user", JSON.stringify(mergedUser));
        if (res.token) {
          localStorage.setItem("token", JSON.stringify(res.token));
        }
        notifyAuthChange();
        closeLoginModal();
      } else {
        toast.error(res?.message || "Google login failed");
      }
    } catch (err) {
      console.error("Google login error:", err);
      toast.error("Google authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setUserEmail("");
    setUserProfilePhoto("");
    notifyAuthChange();
    setDropdownOpen(false);
    openLoginModal();
  };

  return (
    <>
      {loading && <Spinner />}
      <Disclosure as="nav" key={pathname}>
        {() => (
          <div
            className={`md:px-[50px] h-[84px] w-full relative z-10 backdrop-blur-[4px] px-3 ${
              pathname === "/" ? "bg-[#0000009f]" : "bg-black/90"
            } ${
              pathname === "/" && isScrolled
                ? "!fixed Header_animation top-0 left-0 z-50 !bg-black transition-all duration-1000"
                : ""
            }`}
          >
            <div className="flex justify-between items-center h-full">
          {/* Logo — full page reload so home always refreshes */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.location.assign("/");
            }}
            className="inline-block"
          >
            <img
              src={Logo}
              alt="Nutrajun premium natural foods logo"
              className="md:w-[200px] h-[50px] object-contain cursor-pointer"
            />
          </a>

          {/* Nav Links */}
          <div className="hidden lg:flex gap-5">
            {navigation.map((item, i) => (
              <Link
                key={i}
                to={item.href}
                className="text-white text-base px-[18px] py-[13px]"
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Icons */}
          <div className="flex items-center gap-4">
            {userEmail ? (
              <div className="relative">
                <button
                  type="button"
                  aria-label="User account"
                  className="rounded-full flex items-center justify-center h-8 w-8 bg-white cursor-pointer overflow-hidden border-2 border-white/80 shadow-sm focus:outline-none"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  {userProfilePhoto ? (
                    <img
                      src={userProfilePhoto}
                      alt="User avatar"
                      referrerPolicy="no-referrer"
                      className="h-full w-full object-cover rounded-full"
                      onError={() => setUserProfilePhoto("")}
                    />
                  ) : (
                    <span className="text-black font-semibold text-xs">
                      {String(userEmail)[0]?.toUpperCase() || "U"}
                    </span>
                  )}
                </button>

                {/* Dropdown menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <div className="px-4 py-3 bg-emerald-50/70 border-b border-emerald-100/50 flex items-center gap-3">
                      {userProfilePhoto ? (
                        <img
                          src={userProfilePhoto}
                          alt="User avatar"
                          referrerPolicy="no-referrer"
                          className="h-9 w-9 rounded-full object-cover border border-emerald-200 shadow-sm flex-shrink-0"
                          onError={() => setUserProfilePhoto("")}
                        />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shadow-sm flex-shrink-0">
                          {String(userEmail)[0]?.toUpperCase() || "U"}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-800 truncate">
                          {getStoredUser()?.name || "My Account"}
                        </p>
                        <p className="text-[11px] text-gray-500 truncate">{userEmail}</p>
                      </div>
                    </div>
                    <div className="py-1">
                      <Link
                        to={Routing.Profile}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                        onClick={() => setDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <Link
                        to={Routing.OrderList}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                        onClick={() => setDropdownOpen(false)}
                      >
                        My Orders
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <FaUser
                onClick={openLoginModal}
                className="text-white cursor-pointer"
              />
            )}
            <Link to={Routing.Wishlist} className="relative">
              <FaHeart className="text-white" />
            </Link>
            <Link to={Routing.Cart} className="relative inline-flex">
              <FaBagShopping className="text-white relative" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </Link>
            <DisclosureButton className="group h-4">
              <span className="sr-only">Open main menu</span>
              <FaBars
                aria-hidden="true"
                className="text-white lg:hidden block group-data-[open]:hidden cursor-pointer"
              />
              <FaXmark
                aria-hidden="true"
                className="text-white lg:hidden hidden group-data-[open]:block cursor-pointer"
              />
            </DisclosureButton>
          </div>
        </div>

        {/* Mobile Panel */}
        <DisclosurePanel className="lg:hidden bg-[#0000009f] backdrop-blur-[4px] md:px-[50px] w-full absolute left-0 top-full z-50">
          {navigation.map((item, i) => (
            <Link
              key={i}
              to={item.href}
              className="block text-white text-base px-[18px] py-[13px]"
            >
              {item.name}
            </Link>
          ))}
        </DisclosurePanel>
          </div>
        )}
      </Disclosure>

      {/* 🔐 OTP Login / Signup Modal */}
      <Model open={showLoginModal} setOpen={closeLoginModal} size={"sm:max-w-xl"}>
        <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full">
            <img
              alt="Nutrajun natural ingredients company logo"
              src={GreenLogo}
              className="mx-auto h-20 w-auto mb-5"
            />
            <h2 className="my-10 text-center text-2xl font-bold tracking-tight text-primary">
              {localIsSignUp ? "Create an Account" : "Sign in to your account"}
            </h2>
          </div>

          <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
            <div className="px-6 py-12 pt-0 sm:rounded-lg sm:px-12 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-900">
                  Email address
                </label>
                <input
                  type="email"
                  name="email"
                  value={typeof email === "string" ? email : (email ? String(email) : "")}
                  onChange={(e) => {
                    const emailValue = e.target.value;
                    // Always set as string - e.target.value is always a string
                    setEmail(emailValue);
                    console.log("Email input changed:", emailValue);
                  }}
                  onBlur={(e) => {
                    // Ensure email is trimmed on blur
                    const trimmedEmail = e.target.value.trim();
                    if (trimmedEmail !== email) {
                      setEmail(trimmedEmail);
                    }
                  }}
                  className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
                  placeholder="Enter your email"
                  required
                />
              </div>

              {step === 2 && (
                <>
                <div>
                  <label className="block text-sm font-medium text-gray-900">
                    OTP
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
                    placeholder="Enter the OTP sent to your email"
                  />
                </div>

                <div className="text-center text-sm">
                  {otpResendSeconds > 0 ? (
                    <span className="bg-primary text-white px-2">
                      Resend OTP in {otpResendSeconds}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading}
                      className="text-primary font-semibold hover:underline disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>

                  {localIsSignUp && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> After verification, you'll be redirected to complete your profile with name, mobile number, and address details.
                      </p>
                    </div>
                  )}
                </>
              )}

              <CommonBtn
                text={
                  step === 1 
                    ? "Send OTP" 
                    : localIsSignUp 
                    ? "Complete Signup" 
                    : "Verify OTP"
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (step === 1) {
                    if (localIsSignUp) {
                      handleSignUp();
                    } else {
                      handleSendOtp(); // Call without parameters, will use email state
                    }
                  } else {
                    if (localIsSignUp) {
                      handleVerifySignUpOtp();
                    } else {
                      handleVerifyLoginOtp();
                    }
                  }
                }}
                classname="w-full bg-primary text-white flex items-center justify-center h-[40px] hover:bg-link-hover hover:text-black/80"
              />

              <div className="text-center mt-4">
                <span className="text-sm text-gray-600">
                  {localIsSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
                  <button
                    onClick={() => {
                      setLocalIsSignUp(!localIsSignUp);
                      setStep(1); // Reset to step 1 when switching modes
                      setEmail("");
                      setOtp("");
                      setOtpResendSeconds(0);
                    }}
                    className="text-primary font-semibold"
                  >
                    {localIsSignUp ? "Login" : "Sign up"}
                  </button>
                </span>
              </div>

              <GoogleOAuthProvider clientId="1013975691229-bpjfd160jscs0qm4c4ocm58gca7ldnlr.apps.googleusercontent.com">
                <GoogleLogin
                  onSuccess={handleGoogleLoginSuccess}
                  onError={() => {
                    toast.error("Google login failed");
                  }}
                />
              </GoogleOAuthProvider>
            </div>
          </div>
        </div>
      </Model>
    </>
  );
};

export default Header;
